export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

/**
 * Get the user's current cookie consent preferences
 */
export const getConsentPreferences = (): ConsentPreferences | null => {
  try {
    const storedConsent = localStorage.getItem('manivo_cookie_consent');
    if (!storedConsent) return null;
    
    return JSON.parse(storedConsent) as ConsentPreferences;
  } catch (error) {
    console.error('Failed to parse consent preferences:', error);
    return null;
  }
};

/**
 * Check if the user has consented to a specific type of cookie
 */
export const hasConsentForCookieType = (type: keyof ConsentPreferences): boolean => {
  const preferences = getConsentPreferences();
  if (!preferences) return false;
  
  return preferences[type] === true;
};

/**
 * Check if the user has accepted terms and conditions
 */
export const hasAcceptedTerms = (): boolean => {
  return localStorage.getItem('manivo_terms_accepted') === 'true';
};

/**
 * Get the date when terms were accepted
 */
export const getTermsAcceptedDate = (): Date | null => {
  try {
    const dateString = localStorage.getItem('manivo_terms_accepted_date');
    if (!dateString) return null;
    
    return new Date(dateString);
  } catch (error) {
    console.error('Failed to parse terms accepted date:', error);
    return null;
  }
};

/**
 * Get the date when cookie consent was given
 */
export const getConsentDate = (): Date | null => {
  try {
    const dateString = localStorage.getItem('manivo_cookie_consent_date');
    if (!dateString) return null;
    
    return new Date(dateString);
  } catch (error) {
    console.error('Failed to parse consent date:', error);
    return null;
  }
};

/**
 * Clear all consent data (useful for testing or reset)
 */
export const clearAllConsent = (): void => {
  localStorage.removeItem('manivo_terms_accepted');
  localStorage.removeItem('manivo_terms_accepted_date');
  localStorage.removeItem('manivo_cookie_consent');
  localStorage.removeItem('manivo_cookie_consent_date');
};

/**
 * Update cookie preferences (useful for settings page)
 */
export const updateConsentPreferences = (preferences: ConsentPreferences): void => {
  localStorage.setItem('manivo_cookie_consent', JSON.stringify(preferences));
  localStorage.setItem('manivo_cookie_consent_date', new Date().toISOString());
};

/**
 * Check if we should load analytics scripts
 */
export const shouldLoadAnalytics = (): boolean => {
  return hasConsentForCookieType('analytics');
};

/**
 * Check if we should load marketing/advertising scripts
 */
export const shouldLoadMarketing = (): boolean => {
  return hasConsentForCookieType('marketing');
};

/**
 * Check if we should enable personalization features
 */
export const shouldEnablePersonalization = (): boolean => {
  return hasConsentForCookieType('personalization');
}; 